/**
 * dock-drop — the pure decision behind "drag a desktop icon onto the dock".
 *
 * A pointer release during a desktop drag can mean two different things, and
 * only geometry plus the item's own nature can tell them apart. Keeping that
 * choice here (rather than inside the drag maths or the dock component) means
 * the grid never has to know what a dock *is* — it hands over a point, a rect
 * and a kind, and gets back one of a closed set of outcomes.
 *
 * Deliberately DOM-free: no elements, no events, no store. That is what makes
 * the boundary cases (edge-exact drops, a hidden dock, a widget) testable at
 * all — see dock-drop.test.mjs.
 *
 * KEEP IN SYNC with ./dock-drop.d.mts (hand-written type mirror).
 */

/**
 * @typedef {{ x: number, y: number }} Point
 * @typedef {{ left: number, top: number, right: number, bottom: number }} Rect
 * @typedef {"pin" | "grid-move" | "refuse"} DropAction
 * @typedef {null | "widget" | "already-pinned" | "unavailable"} RefuseReason
 * @typedef {{ action: DropAction, reason: RefuseReason, overDock: boolean, message: string | null }} DropDecision
 * @typedef {"reorder" | "unpin" | "none"} DockDragAction
 * @typedef {{ action: DockDragAction, index: number }} DockDragDecision
 */

/**
 * How far outside the dock's own box a drop still counts as "on the dock".
 *
 * The dock is ~69px tall and sits 18px off the bottom of the screen, so the
 * literal rect is a thin ribbon with a dead strip underneath it that looks
 * like part of the dock and isn't. Aiming at a 69px target while the payload
 * is a 92px tile the pointer is nowhere near the centre of is how a drop that
 * "should obviously have landed" silently becomes a grid move — the icon flies
 * back and nothing happens, which reads exactly like a broken feature.
 *
 * Padding the hit rect rather than the element keeps the dock's PAINTED size
 * honest: the affordance still lights up the real dock, the tolerance is
 * invisible, and nothing about the layout shifts.
 */
export const DOCK_HIT_PAD = 26;

/**
 * Point-in-rect with INCLUSIVE edges.
 *
 * The dock is a small target and its border is exactly where a user aiming at
 * it tends to release. Treating the boundary as "outside" would silently turn
 * a deliberate pin into a grid move at the one pixel most likely to be hit.
 *
 * A zero-area rect is never hit: that is how a hidden or unmounted dock
 * presents (getBoundingClientRect on a display:none element is all zeros), and
 * a dock that isn't on screen must not swallow drops.
 *
 * @param {Point | null | undefined} point
 * @param {Rect | null | undefined} rect
 * @returns {boolean}
 */
export function pointInRect(point, rect) {
  if (!point || !rect) return false;
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
  const w = rect.right - rect.left;
  const h = rect.bottom - rect.top;
  if (!(w > 0) || !(h > 0)) return false;
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
}

/**
 * Grow a rect outwards by `pad` on every side.
 *
 * A null rect stays null (no dock, no target) and a ZERO-AREA rect stays
 * refused rather than becoming a `2*pad` square: a display:none dock reports
 * all-zeros, and inflating that would hand a hidden dock a live hit box in the
 * top-left corner of the screen — the one place a user never means to drop.
 *
 * @param {Rect | null | undefined} rect
 * @param {number} pad
 * @returns {Rect | null}
 */
export function expandRect(rect, pad) {
  if (!rect) return null;
  const w = rect.right - rect.left;
  const h = rect.bottom - rect.top;
  if (!(w > 0) || !(h > 0)) return null;
  const p = Number.isFinite(pad) ? pad : 0;
  return {
    left: rect.left - p,
    top: rect.top - p,
    right: rect.right + p,
    bottom: rect.bottom + p,
  };
}

/**
 * Decide what a drop at `point` means.
 *
 * `overDock` is reported separately from the action because the two answer
 * different questions: the action is what to DO, `overDock` is whether the
 * dock owns the pointer right now. The caller needs the latter to suppress the
 * grid's landing-slot preview — a refused widget still must not show a grid
 * slot underneath a highlighted dock, or the user is told two contradictory
 * things at once.
 *
 * @param {{
 *   point?: Point | null,
 *   dockRect?: Rect | null,
 *   kind?: "icon" | "widget" | string,
 *   pinned?: boolean,
 *   visible?: boolean,
 *   label?: string,
 * }} input
 * @returns {DropDecision}
 */
export function decideDrop(input) {
  const { point, dockRect, kind, pinned, visible, label } = input || {};
  const over = pointInRect(point, dockRect);
  if (!over) return { action: "grid-move", reason: null, overDock: false, message: null };

  const name = label && label.trim() ? label.trim() : "That app";

  // The dock holds apps. A widget is a live surface with a size; there is
  // nowhere in a row of 26px glyphs for it to go, so say so rather than
  // dropping it into a slot it would not fit.
  if (kind === "widget") {
    return {
      action: "refuse",
      reason: "widget",
      overDock: true,
      message: "Widgets can't be pinned to the Dock — the Dock holds apps.",
    };
  }

  // Fail closed on visibility. `visible` is the caller's view of the
  // SERVER-FILTERED package list; an app this account cannot see must not
  // acquire a pin, or the pin outlives the entitlement in localStorage.
  if (visible === false) {
    return {
      action: "refuse",
      reason: "unavailable",
      overDock: true,
      message: `${name} isn't available for your account`,
    };
  }

  // Already pinned is a no-op, but a SILENT no-op reads as a broken drop.
  if (pinned) {
    return {
      action: "refuse",
      reason: "already-pinned",
      overDock: true,
      message: `${name} is already in the Dock`,
    };
  }

  return { action: "pin", reason: null, overDock: true, message: null };
}

/**
 * Where a pin would be inserted, given the dock's existing item rects.
 *
 * Midpoint rule: the pointer belongs after every item whose horizontal centre
 * it has passed. Returns 0..items.length, so it is directly usable as a
 * splice index and as the position of a rendered insertion caret.
 *
 * `itemRects` must cover ONLY the reorderable pins. The dock also renders a
 * trailing Applications button, and measuring it would let the caret reach an
 * index one past the last pin — a position no reorder can honour.
 *
 * The caret this drives is rendered out of flow (absolutely positioned). An
 * in-flow caret would widen the dock by its own width and shift every item
 * right by a gap, moving the very midpoints measured here — so the caret would
 * oscillate between two indices as the pointer sat still.
 *
 * @param {Point | null | undefined} point
 * @param {ReadonlyArray<{ left: number, right: number }>} itemRects
 * @returns {number}
 */
export function dockInsertIndex(point, itemRects) {
  if (!point || !itemRects || !itemRects.length) return 0;
  let i = 0;
  for (const r of itemRects) {
    const mid = (r.left + r.right) / 2;
    if (point.x > mid) i++;
    else break;
  }
  return Math.max(0, Math.min(itemRects.length, i));
}

/**
 * Move `appId` to the gap at `insertIndex`, returning the new pin order.
 *
 * `insertIndex` is a GAP index measured against the list as the user currently
 * sees it — dragged item still in place — because that is the only list the
 * caret can be drawn over. Splicing at that index after removing the item is
 * the classic off-by-one: every gap to the right of the item's own slot loses
 * one position when the item leaves. Hence the `from < insertIndex` decrement.
 *
 * Returns the SAME array reference for a no-op so callers can skip the write
 * and the re-render. Both gaps adjacent to an item (its own left and right
 * edges) resolve to where it already is, which is what makes a small jiggle
 * during a drag do nothing instead of churning localStorage.
 *
 * @param {ReadonlyArray<string>} pins
 * @param {string} appId
 * @param {number} insertIndex
 * @returns {string[] | ReadonlyArray<string>}
 */
export function reorderPins(pins, appId, insertIndex) {
  if (!Array.isArray(pins) || !appId) return pins;
  const from = pins.indexOf(appId);
  if (from < 0) return pins;

  const gap = Math.max(0, Math.min(pins.length, Math.trunc(Number(insertIndex) || 0)));
  const to = gap > from ? gap - 1 : gap;
  if (to === from) return pins;

  const next = pins.slice();
  next.splice(from, 1);
  next.splice(to, 0, appId);
  return next;
}

/**
 * What a release means for a pin dragged FROM the dock.
 *
 * Dragging a pin off the dock unpins it — the natural inverse of dragging an
 * icon on, and the only direct-manipulation way to remove one. It is gated on
 * the same padded rect the pin-drop uses, so the boundary at which an app stops
 * being "in the dock" is a single number rather than two that can drift apart.
 *
 * `moved` distinguishes a drag from a click. Without it every launch of a
 * docked app would also be a zero-distance "drag" that unpins it, because the
 * pointer never left the dock's rect and the release still has to mean
 * something.
 *
 * @param {{
 *   point?: Point | null,
 *   dockRect?: Rect | null,
 *   moved?: boolean,
 *   itemRects?: ReadonlyArray<{ left: number, right: number }>,
 * }} input
 * @returns {DockDragDecision}
 */
export function decideDockDrag(input) {
  const { point, dockRect, moved, itemRects } = input || {};
  if (!moved) return { action: "none", index: 0 };
  if (!pointInRect(point, dockRect)) return { action: "unpin", index: 0 };
  return { action: "reorder", index: dockInsertIndex(point, itemRects || []) };
}
